import axios, { AxiosInstance } from 'axios';
import * as API from '../../../constants/API';
import { InterestCheckRecord } from '../../../constants/Records';

export class InterestApiClient {
  private api: AxiosInstance;

  static async ClientFactory(): Promise<InterestApiClient> {
    return new InterestApiClient();
  }

  constructor() {
    const apiKey = process.env.REACT_APP_INTEREST_API_KEY || '';

    this.api = axios.create({
      baseURL: API.interestApiRoot,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
    });
  }

  // レコード件数取得（効率的なカウント用）
  async getRecordCount(uniqueId: string): Promise<number> {
    const res = await this.api.get(`/interests/users/${uniqueId}/records`);
    // { total } 形式の場合
    if (res.data && typeof res.data.total === 'number') {
      return res.data.total;
    }
    // 配列の場合
    if (Array.isArray(res.data)) {
      return res.data.length;
    }
    // { data: [] } 形式の場合
    if (res.data && Array.isArray(res.data.data)) {
      return res.data.data.length;
    }
    return 0;
  }

  // レコード一覧取得
  async getRecords(uniqueId: string): Promise<InterestCheckRecord[]> {
    const res = await this.api.get(`/interests/users/${uniqueId}/records`);
    // APIが配列を直接返す場合
    if (Array.isArray(res.data)) {
      return res.data;
    }
    // { total, data } 形式で返す場合
    if (res.data && Array.isArray(res.data.data)) {
      return res.data.data;
    }
    // totalのみ返す場合は、各レコードを個別に取得する必要がある
    if (res.data && typeof res.data.total === 'number' && res.data.total > 0) {
      const records: InterestCheckRecord[] = [];
      for (let seq = 1; seq <= res.data.total; seq++) {
        try {
          const record = await this.getRecord(uniqueId, seq);
          records.push(record);
        } catch (e) {
          // 欠番の場合はスキップ
          continue;
        }
      }
      return records;
    }
    return [];
  }

  // 特定レコード取得
  async getRecord(uniqueId: string, sequence: number): Promise<InterestCheckRecord> {
    const res = await this.api.get(`/interests/users/${uniqueId}/records/${sequence}`);
    return res.data;
  }

  // 最新レコード取得
  async getLatestRecord(uniqueId: string): Promise<InterestCheckRecord> {
    const res = await this.api.get(`/interests/users/${uniqueId}/records/latest`);
    return res.data;
  }

  // 期間指定取得
  async getRecordsByDateRange(uniqueId: string, from: string, to: string): Promise<InterestCheckRecord[]> {
    const res = await this.api.get(`/interests/users/${uniqueId}/records/from/${from}/to/${to}`);
    return res.data;
  }

  // レコード作成
  async createRecord(uniqueId: string, data: Partial<InterestCheckRecord>): Promise<InterestCheckRecord> {
    const res = await this.api.post(`/interests/users/${uniqueId}/records`, data);
    return res.data;
  }

  // レコード更新
  async updateRecord(
    uniqueId: string,
    sequence: number,
    data: Partial<InterestCheckRecord>,
  ): Promise<InterestCheckRecord> {
    const res = await this.api.put(`/interests/users/${uniqueId}/records/${sequence}`, data);
    return res.data;
  }

  // レコード削除
  async deleteRecord(uniqueId: string, sequence: number): Promise<void> {
    await this.api.delete(`/interests/users/${uniqueId}/records/${sequence}`);
  }
}
